import { api } from "../../../api/API";
import { Task, TaskLink } from "../../../api/Tasks";
import { useSnackAction } from "../../../misc/SnackAction";
import { TaskPicker } from "../TaskPicker";
import { TaskLinkSection, toLink } from "./TaskLinkSection";

// direction says which end of the edge the listed tasks sit on: "blocked-by" lists
// the tasks this one waits on, "followed-by" the tasks waiting on this one.
export type LinkDirection = "blocked-by" | "followed-by";

export function TaskLinks(props: {
    placeholder: string;
    taskId: number;

    // team and project are handed to a task created from here, so a related task lands
    // in the same place as the one it was made from
    team: number;
    project: number | null;

    direction: LinkDirection;
    value: TaskLink[];
    onChange: (value: TaskLink[]) => void;
    onOpen: (task: TaskLink) => void;
}) {
    const taken = props.value.map(link => link.id).concat(props.taskId);

    const edge = (other: number) => props.direction === "blocked-by"
        ? { task: props.taskId, dependsOn: other }
        : { task: other, dependsOn: props.taskId };

    const add = useSnackAction(async (input: { task?: Task; title?: string }) => {
        const task = input.task || await api.tasks.upsert({
            type: "task",
            title: input.title,
            team: props.team,
            project: props.project,
        });
        await api.tasks.setDependency({ ...edge(task.id), archived: false });

        props.onChange(props.value.concat(toLink(task)));
    }, { success: "Added" });

    const remove = useSnackAction(async (input: { id: number }) => {
        await api.tasks.setDependency({ ...edge(input.id), archived: true });

        props.onChange(props.value.filter(link => link.id !== input.id));
    }, { success: "Removed" });

    const toggle = useSnackAction(async (input: { id: number; completed: boolean }) => {
        const saved = await api.tasks.complete(input);

        props.onChange(props.value.map(link => link.id === saved.id ? toLink(saved) : link));
    }, { success: "Saved" });

    // the list holds a summary, so the whole task is loaded to archive it
    const archive = useSnackAction(async (input: { id: number }) => {
        const task = await api.tasks.get({ id: input.id });
        await api.tasks.upsert({ ...task, archived: true });

        props.onChange(props.value.filter(link => link.id !== input.id));
    }, { success: "Archived" });

    const busy = add.loading || remove.loading || toggle.loading || archive.loading;

    return (
        <TaskLinkSection
            value={props.value}
            busy={busy}
            onOpen={props.onOpen}
            onToggle={(link, completed) => toggle.callback({ id: link.id, completed: completed })}
            onRemove={link => remove.callback({ id: link.id })}
            onArchive={link => archive.callback({ id: link.id })}
        >
            <TaskPicker
                placeholder={props.placeholder}
                disabled={busy}
                exclude={taken}
                onPick={task => add.callback({ task: task })}
                onCreate={title => add.callback({ title: title })}
            />
        </TaskLinkSection>
    );
}
